import { inject, Injectable } from '@angular/core';
import { addNodeToJson, sort, WS_CONSTANTS } from '@wms/core';
import { VARIABLE_CONSTANTS, SWAGGER_CONSTANTS, SERVICE_TYPE_CONSTANTS } from '@wms/variables';
import { find, forEach, get, includes, isEmpty, isObject, last, split, toLower } from 'lodash-es';

import { BaseComponent } from "../../components/base/base.component";
import { ProjectService } from '../project/project.service';
import { WebserviceService } from '../webservices/webservice.service';

@Injectable({
  providedIn: 'root'
})
export class ServiceFactoryService extends BaseComponent {

  /*Array to hold service objects*/
  services: any = [];
  projectId: any;
  requestQueue: any = {
    'services': [],
    'operations': {},
    'serviceDef': {},
    'prefabTypes': {}
  };
  serviceDefMap: any = {};
  supportedOperations = WS_CONSTANTS.HTTP_METHODS.map((method) => { return method.toLowerCase(); });
  primitiveDataTypes = WS_CONSTANTS.PRIMITIVE_DATA_TYPES;
  IS_LIST_KEY = 'x-WM-IS_LIST';
  UNIQUE_ITEMS_KEY = 'uniqueItems';
  FULLY_QUALIFIED_NAME_KEY = 'x-WM-FULLY_QUALIFIED_NAME';
  FULLY_QUALIFIED_TYPE_KEY = 'x-WM-FULLY_QUALIFIED_TYPE';
  WM_SCHEMA = 'x-WM-SCHEMA';
  parameterTypeKey = 'in';
  AUTH_BASIC_TYPE = VARIABLE_CONSTANTS.REST_SERVICE.SECURITY_DEFN_BASIC;
  prefabDataTypes = {};
  webService: any;
  databaseNames: any = [];
  servicesTree: any = [{
    "idkey": "LABEL_SERVICES",
    "id": "All Services",
    "uid": "0",
    "nodeMap": {
      "0": "All Services"
    }
  }];
  datamodelNames: any = [];

  constructor() {
    super();
    this.webService = inject(WebserviceService);
    this.projectId = this.projectService.getProjectId();
  }

  /*function to get service object matching its name*/
  getServiceObjectByName(name: string) {
    let serviceObject = { operations: [], type: '' };
    forEach(this.services, (serviceObj: any) => {
      if (serviceObj.name === name) {
        serviceObject = serviceObj;
      }
    });
    return serviceObject;
  }

  /*function to get service object matching its name*/
  removeService(name: any) {
    this.services = this.services.filter((service: any) => service?.name !== name)
    this.serviceDefMap[name] = undefined;
  }

  /*function to get service operation object matching its name*/
  getServiceOperationObjectById(serviceName: string, operationId: any, mode?: any) {
    let serviceObj = this.getServiceObjectByName(serviceName);
    if (mode && mode.isCrud === true) {
      if (mode.mode === 'all') {
        return serviceObj.operations.filter((val: any) => {
          return val.crudOperationId === operationId;
        });
      } else if (mode.mode === 'createorupdate') {
        return serviceObj.operations.filter((val: any) => {
          return (val.operationType === 'create' || val.operationType === 'update') && val.crudOperationId === operationId;
        })[0];
      } else {
        return serviceObj.operations.filter((val: any) => {
          return val.operationType === mode.mode && val.crudOperationId === operationId;
        })[0];
      }
    } else {
      return (operationId ? (find(serviceObj.operations, { 'operationId': operationId })) : serviceObj.operations[0]) || {};
    }
  }

  extractParamInfoFromRef(param: any, swaggerObject: any) {
    let extractedParam;
    let parts = param.$ref.replace('#/', '').split('/');
    parts[parts.length - 1] = '["' + parts[parts.length - 1] + '"]';
    extractedParam = get(swaggerObject, parts.join(''));
    return extractedParam;
  }

  /**
   * function to get X-WM-FULLY_QUALIFIED_NAME from a responses object or a body param inside a Swagger
   *  path which contains fields directly inside it without $ref links
   */
  getFQNFromSchema(baseObj: any) {
    let fqn;
    if (baseObj) {
      fqn = get(baseObj, "schema[x-WM-FULLY_QUALIFIED_NAME]") || get(baseObj, "schema.items[x-WM-FULLY_QUALIFIED_NAME]");
    }
    return fqn;
  }

  /*function to get list of services from the backend*/
  getServicesWithType(reloadFlag?: any) {
    /*if services already fetched once and reloadFlag not set, return the fetched services*/
    if (this.services.length > 0 && !reloadFlag) {
      return Promise.resolve(this.services);
    } else {
      /*data for the request*/
      let urlParams = {
        projectID: this.projectId || this.projectService.getProjectId(),
        showSpinner: false
      };

      return (this.webService.listServicesWithType(urlParams) as Promise<any>).then((resp: any) => {
        resp = sort(resp, 'name');
        this.services = [];
        this.databaseNames = [];
        resp.forEach((serviceObj: any) => {
          this.services.push({
            type: serviceObj.type,
            name: serviceObj.name,
            url: serviceObj.url,
            isMockEnabled: serviceObj.mockEnabled,
            operations: []
          });
          if (serviceObj.type === VARIABLE_CONSTANTS.SERVICE_TYPE_DATA) {
            /*Add the databaseName to the list of database names.*/
            if (!this.databaseNames.includes(serviceObj.name)) {
              /*Add the database name to the array so that it can be used while generating default names during design database.*/
              this.databaseNames.push(serviceObj.name);
            }
          }
        });

        return Promise.resolve(this.services);
        // triggerFn(successCallBack, this.services); --calls are invoked twice
      }).catch((err: any) => {
        return Promise.reject(err);
      })
    }
  }

  clearServiceOperations(serviceId: any) {
    forEach(this.services, (serviceObj: any) => {
      if (serviceObj.name === serviceId) {
        serviceObj.operations = [];
      }
    });
    this.serviceDefMap[serviceId] = undefined;
  }

  /*function to get the full serviceDef (api doc)*/
  getServiceDef(serviceId: any, forceReload?: boolean) {
    let callback,
      urlParams = {
        "urlParams": {
          serviceId: serviceId,
          projectId: this.projectId || this.projectService.getProjectId()
        }
      };
    if (this.serviceDefMap[serviceId] && !forceReload) {
      /*if operations are already present, return the cached response*/
      return Promise.resolve(this.serviceDefMap[serviceId]);
    }
    if (forceReload) {
      this.resetServiceOperations(serviceId);
    }

    /*invoking a service to get the operations that a particular service has and it's
     * parameters to create a unique url pattern*/
    return (this.webService.retrieveServiceOperations(urlParams) as Promise<any>).then((response: any) => {
      response.paths = sort(response.paths, 'x-WM-COMPLETE_PATH');
      this.serviceDefMap[serviceId] = response;
      return Promise.resolve(response);
    }).catch((errMsg: any) => {
      return Promise.reject(errMsg);
    });
  }

  /**
 * resets the cached operation info for a service
 * @param serviceName
 */
  resetServiceOperations(serviceName: string) {
    this.getServiceObjectByName(serviceName).operations.length = 0;
  }

  /*Getting the fully qualified name*/
  getFullyQualifiedName(refValue: any, definitions: any) {
    let returnType;
    refValue = refValue.split('/').pop();
    returnType = definitions[refValue];
    returnType = returnType ? returnType[this.FULLY_QUALIFIED_NAME_KEY] : 'Object';
    return returnType;
  }

  getReturnType(responseObject: any, definitions: any) {
    let type;
    if (responseObject.type) {
      /*In case of primitive type*/
      if (responseObject.type !== 'array') {
        return responseObject.type;
      }
      /*In case of list type*/
      if (responseObject.items.type) {
        type = responseObject.items.type;
      } else {
        if (responseObject.items.$ref) {
          type = this.getFullyQualifiedName(responseObject.items.$ref, definitions);
        }
      }
      return type;
    } else {
      /*In case of object type*/
      if (responseObject.$ref) {
        return this.getFullyQualifiedName(responseObject.$ref, definitions);
      }
    }
  }

  isRESTSupported(type: any) {
    return (VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES.indexOf(type) !== -1);// || VARIABLE_CONSTANTS.SERVICE_TYPE_DATA === type);
  }

  //Check if variable/operation is a query type and of put/post type
  isBodyTypeQueryProcedure(variable: any) {
    return (includes(['QueryExecution', 'ProcedureExecution'], variable.controller)) && (includes(['put', 'post'], variable.operationType));
  }

  //Return params from swagger for post/put query types
  getRawParams(operationObj: any, definitions: any) {
    let refValue,
      refKey: any,
      defObj: any,
      operationList: any = [];
    //Handle multipart/form-data request of query/procedure
    if (includes(operationObj.consumes, WS_CONSTANTS.CONTENT_TYPES.MULTIPART_FORMDATA)) {
      forEach(operationObj.parameters, (param) => {
        if (param.name === SWAGGER_CONSTANTS.WM_DATA_JSON) {
          refValue = get(param, ['x-WM-SCHEMA', '$ref']);
        } else {
          operationList.push(param);
        }
      });
    } else if (operationObj.parameters.length > 1) {
      return operationObj.parameters;
    } else {
      refValue = get(operationObj, ['parameters', 0, 'schema', '$ref']);
    }
    refKey = last(split(refValue, '/'));
    defObj = definitions[refKey];
    forEach(defObj && defObj.properties, (value, key) => {
      //Ignore readOnly params
      if (value.readOnly) {
        return;
      }
      value.name = key;
      value[this.parameterTypeKey] = VARIABLE_CONSTANTS.BODY_FIELD;
      value.required = includes(defObj.required, key);
      operationList.push(value);
    });
    return operationList;
  }

  processOperations(serviceObj: any, operations?: any, swagger?: any) {
    let paramsKey,
      isRestSupportedService = this.isRESTSupported(serviceObj.type),
      definitions: any,
      securityDefinitions: any,
      schemaObject: any,
      typeArgumentsObject: any;

    /*Empty the "operations" so that they are set based on the response.*/
    serviceObj.operations = [];

    if (isRestSupportedService) {
      definitions = swagger.definitions;
      securityDefinitions = swagger.securityDefinitions;
    }

    function getFormatFromMap(format: any) {
      let MAP: any = {
        "int32": "integer",
        "int64": "long"
      };

      return MAP[format] || format;
    }

    /*loop through the operations to append them to the respective service object*/
    forEach(operations, (operation) => {
      /*variable to determine if the fetched operation already exists*/
      let operationObject: any = {},
        isList,
        typeRef: any,
        format: any,
        returnObj,
        returnType,
        returnFormat,
        rawParameters,
        fullyQualifiedName,
        isDbServiceOp = (type: any) => {
          return type === "hqlquery" || type === "nativequery" || type === "procedure";
        };

      if (isDbServiceOp(operation.operationType)) {
        returnType = operation.return;
      } else {
        // fetch return type and operation object from swagger
        if (get(operation, "responses['200'].schema")) {
          schemaObject = operation.responses['200'].schema;
          typeArgumentsObject = schemaObject["x-WM-TYPE_ARGUMENTS"];
          isList = schemaObject.$ref === "#/definitions/Page";
          /*If the typeArguments is specified obtain the return type from it*/
          if (typeArgumentsObject && typeArgumentsObject[0]) {
            schemaObject = typeArgumentsObject[0];
          }
          returnType = this.getReturnType(schemaObject, definitions);
          returnFormat = schemaObject.format;
          if (!isList) {
            isList = schemaObject.type && schemaObject.type === 'array';
          }
        } else if (!isEmpty(operation['x-WM-OUT-PARAMS-TYPE'])) {
          returnType = this.getFullyQualifiedName(operation['x-WM-OUT-PARAMS-TYPE'][0], definitions);
        } else {
          returnType = "void";
        }
        returnFormat = getFormatFromMap(returnFormat);
      }
      fullyQualifiedName = this.getFQNFromSchema(get(operation, "responses['200']"));
      if (fullyQualifiedName) {
        returnType = fullyQualifiedName;
      }

      /* special case for pageable return type */
      if (isRestSupportedService) {
        returnObj = { typeRef: returnType };
        paramsKey = "parameters";

      } else {
        returnObj = returnType;
        paramsKey = "parameter";
        this.IS_LIST_KEY = 'isList';
        this.parameterTypeKey = 'parameterType';
      }

      /*push the operation only if does not exist previously*/
      operationObject = {
        name: operation[WS_CONSTANTS.OPERATION_NAME_KEY] || operation.operationId || operation.name,
        operationType: operation['x-WM-CRUD_OPERATION_ID'] ? operation['x-WM-OPERATION_TYPE'] : operation.operationType || null,
        parameter: undefined,
        isList: isList,
        return: returnObj,
        returnFormat: returnFormat,
        controller: operation.tags && operation.tags[0].replace(/Controller$/, ''),
        operationId: operation.operationId,
        consumes: operation.consumes,
        deprecated: operation.deprecated,
        entityName: operation['x-WM-CRUD_OPERATION_ID'] ? operation.tags[0] : '',
        crudOperationId: operation['x-WM-CRUD_OPERATION_ID']
      };
      serviceObj.operations.push(operationObject);

      /* process the operation params as well */
      if (!isEmpty(operation[paramsKey])) {
        operationObject.parameter = [];
        //For post/put query methods get params from definitions
        if (this.isBodyTypeQueryProcedure(operationObject)) {
          rawParameters = this.getRawParams(operation, definitions);
        } else {
          rawParameters = operation[paramsKey];
        }
        let extractedParam;
        forEach(rawParameters, (param) => {
          if (param.$ref) {
            extractedParam = this.extractParamInfoFromRef(param, swagger);
            if (extractedParam && extractedParam.in) {
              param = extractedParam;
            }
          }

          isList = param[this.IS_LIST_KEY];

          /* special cases for MultiPart type params */
          if (toLower(param[this.parameterTypeKey]) === "formdata") {
            if (param.type === "string" && param[this.WM_SCHEMA]) {
              typeRef = this.getReturnType(param[this.WM_SCHEMA], definitions);
            } else if (param.type === "ref") {
              typeRef = param['x-WM-FULLY_QUALIFIED_TYPE'];
            } else if (param.type === 'array') {
              isList = true;
              typeRef = param.items && param.items.type;
            } else {
              typeRef = param.type;
            }
          } else {
            if (param.type) {
              if (param.type === "array") {
                isList = true;
                typeRef = param.items && param.items.type;
                format = param.items && param.items.format;
              } else {
                typeRef = param.type;
                format = param.format;
              }
            } else {
              if (param.schema) {
                isList = param.schema.type === 'array';
                typeRef = this.getReturnType(param.schema, definitions);
                format = param.schema.format;
              } else if (param.items) {
                typeRef = this.getReturnType(param.items, definitions);
              }
            }
            fullyQualifiedName = this.getFQNFromSchema(param);
            if (fullyQualifiedName) {
              typeRef = fullyQualifiedName;
            }
          }
          format = getFormatFromMap(format);

          /* push the param info into operation object */
          operationObject.parameter.push({
            name: param.name,
            typeRef: typeRef,
            format: format,
            isList: isList,
            parameterType: param[this.parameterTypeKey]
          });
        });
      } else {
        operationObject.parameter = [];
      }

      /* push an extra RequestBody param for WebSocketService */
      if (!isEmpty(operation['x-WM-IN-PARAMS-TYPE'])) {
        operationObject.parameter = operationObject.parameter || [];
        operationObject.parameter.push({
          name: 'RequestBody',
          typeRef: this.getFullyQualifiedName(operation['x-WM-IN-PARAMS-TYPE'][0], definitions),
          parameterType: 'BODY'
        });
      }

      if (this.isBasicAuthAvailable(securityDefinitions, operation.security, swagger)) {
        if (!operationObject.parameter) {
          operationObject.parameter = [];
        }
        operationObject.parameter.push({
          "name": "wm_auth_username",
          "parameterType": "auth"
        });
        operationObject.parameter.push({
          "name": "wm_auth_password",
          "parameterType": "auth"
        });
      }
    });
  }

  /*function to get list of operations for a service from the backend*/
  getServiceOperations(serviceId: any, reloadFlag?: any) {
    /*sanity checking of the params*/
    if (!serviceId || serviceId === '') {
      return;
    }
    reloadFlag = !(!reloadFlag || reloadFlag === 'false');

    /*get the required service object*/
    let serviceObj: any = this.getServiceObjectByName(serviceId),
      urlParams,
      operations: any,
      onOperationsFetch = (operations: any, swagger?: any) => {
        this.processOperations(serviceObj, operations, swagger);
        return Promise.resolve(serviceObj.operations);
      };

    /*if service's operations already fetched once and reloadFlag not set, return the fetched operations*/
    if (serviceObj.operations.length > 0 && !reloadFlag) {
      return Promise.resolve(serviceObj.operations);
    }

    if (this.isRESTSupported(serviceObj.type)) {
      operations = [];

      /* invoking a service to get the operations that a particular service has and it's
       * parameters to create a unique url pattern
       */
      return this.getServiceDef(serviceId, reloadFlag)?.then((response: any) => {
        /*iterate over the paths received from the service response*/
        forEach(response.paths, (path) => {
          /*iterate over the operations available in each path*/
          forEach(this.supportedOperations, (operation) => {
            if (path[operation]) {
              path[operation].operationType = operation;
              path[operation].relativePath = path['x-WM-RELATIVE_PATH'];

              /* set operationType for Query/Procedure operations */
              if (path[operation].tags && path[operation].tags[0] === WS_CONSTANTS.CONTROLLER_NAMES.PROCEDURE_CONTROLLER) {
                path[operation].serviceSubType = "procedure";
              } else if (path[operation].tags && path[operation].tags[0] === WS_CONSTANTS.CONTROLLER_NAMES.QUERY_CONTROLLER) {
                path[operation].serviceSubType = "query";
                /* here we have to set operationType to either hqlquery or nativequery (have to check how)*/
              }
              operations.push(path[operation]);
            }
          });
        });
        return onOperationsFetch(operations, response);
      }).catch((error: any) => {
        /*handle error response*/ //TODO: Add toaster
        // wmToaster.show("error", this.locale["MESSAGE_ERROR_TITLE"], this.locale["MESSAGE_ERROR_FETCH_SERVICE_METHODS_DESC"]);
        // onOperationFetchError();
        return Promise.reject(error);
      });
    } else {
      /*data for the request*/
      urlParams = {
        urlParams: {
          projectID: this.projectId || this.projectService.getProjectId(),
          serviceID: serviceId
        }
      };
      /*Get the operations for the service*/
      return (this.webService.getServiceOperations(urlParams) as Promise<any>).then((response: any) => {
        return onOperationsFetch(response);
      }).catch((error: any) => {
        return Promise.reject(error);
      });
    }
  }

  getCrudServiceOperationParams(serviceId: any, operationId: any, mode: any, reloadFlag?: any) {
    /*sanity checking of the params*/
    if (isEmpty(serviceId)) {
      return Promise.resolve();
    }
    reloadFlag = !(!reloadFlag || reloadFlag === 'false');

    /*get the required operation object*/
    let serviceObj: any = this.getServiceObjectByName(serviceId),
      operationObj: any = this.getServiceOperationObjectById(serviceId, operationId, { mode: mode, isCrud: true }),
      urlParams

    /*if service's operation's params already fetched once and reloadFlag not set, return the fetched params*/
    if ((operationObj && operationObj.parameter || (operationObj && operationObj[0] && operationObj[0].parameter)) && !reloadFlag) {
      return Promise.resolve(operationObj);
    }

    if (this.isRESTSupported(serviceObj.type)) {
      return this.getServiceOperations(serviceId, true)?.then(() => {
        operationObj = this.getServiceOperationObjectById(serviceId, operationId, { mode: mode, isCrud: true });
        return Promise.resolve(operationObj);
      });
    }

    else {
      urlParams = {
        urlParams: {
          projectID: this.projectId || this.projectService.getProjectId(),
          serviceID: serviceId,
          operationID: operationId
        }
      };
      /*call web service to fetch the data*/
      return (this.webService.getServiceOperationParams(urlParams) as Promise<any>).then((response: any) => {
        return Promise.resolve(operationObj)
      }).catch((error) => {
        return Promise.reject(error)
      });
    }
    return Promise.resolve();
  }

  /*function to get list of params and return type for an operation of a service from the backend*/
  getServiceOperationParams(serviceId: any, operationId: any, reloadFlag?: any) {
    return new Promise<void>((res, rej) => {
      /*sanity checking of the params*/
      if (isEmpty(serviceId)) {
        res();
        return;
      }
      reloadFlag = !(!reloadFlag || reloadFlag === 'false');

      /*get the required operation object*/
      let serviceObj: any = this.getServiceObjectByName(serviceId),
        operationObj: any = this.getServiceOperationObjectById(serviceId, operationId),
        urlParams,
        onOperationParamsFetch = () => {
          res(operationObj);
        },
        onOperationParamsFetchError = () => {
          rej();
        };

      /*if service's operation's params already fetched once and reloadFlag not set, return the fetched params*/
      if (operationObj.parameter && !reloadFlag) {
        res(operationObj);
        return;
      }

      if (this.isRESTSupported(serviceObj.type)) {
        this.getServiceOperations(serviceId, true)?.then(() => {
          operationObj = this.getServiceOperationObjectById(serviceId, operationId);
          res(operationObj);
        });
      } else {
        urlParams = {
          urlParams: {
            projectID: this.projectId || this.projectService.getProjectId(),
            serviceID: serviceId,
            operationID: operationId
          }
        };
        /*call web service to fetch the data*/
        (this.webService.getServiceOperationParams(urlParams) as Promise<any>).then((response: any) => {
          onOperationParamsFetch();
        }).catch(() => {
          onOperationParamsFetchError();
        });
      }
    });
  }

  getServiceOperationInfo(selectedOperation: any, selectedService: any): Promise<any> {
    let operationInfo: any = {};

    return this.getServiceDef(selectedService).then((response) => {
      /*iterate over the paths received from the service response*/
      let pathsArr = Object.keys(response.paths),
        securityDefinitions = response.securityDefinitions,
        paramDataType,
        i,
        nPaths,
        pathKey,
        path,
        j,
        nOps,
        opType,
        operation;
      for (i = 0, nPaths = pathsArr.length; i < nPaths; i++) {
        pathKey = pathsArr[i];
        path = response.paths[pathKey];
        for (j = 0, nOps = this.supportedOperations.length; j < nOps; j++) {
          opType = this.supportedOperations[j];
          operation = path[opType];
          if (operation && operation[WS_CONSTANTS.OPERATION_NAME_KEY] === selectedOperation) {
            operationInfo.httpMethod = opType;
            operationInfo.operationId = operation.operationId;
            operationInfo.name = selectedOperation;
            operationInfo.relativePath = (path[VARIABLE_CONSTANTS.REST_SERVICE.BASE_PATH_KEY] || "") + path[VARIABLE_CONSTANTS.REST_SERVICE.RELATIVE_PATH_KEY];
            /*saving the request mime type only if it is explicitly mentioned used in the file upload widget to decide the mime type from swagger path object*/
            if (operation.consumes && operation.consumes.length) {
              operationInfo.consumes = operation.consumes;
            }
            /*
             * saving the response mime type only if it is explicitly mentioned.
             * UseCase: 'download' operation of 'FileService' gives application/octet-stream
             * this is used to determine if a download file has to be simulated through form submit(as download not possible through AJAX)
             */
            if (operation.produces && operation.produces.length) {
              operationInfo.produces = operation.produces;
            }
            operationInfo.parameters = [];

            if (operation.parameters && operation.parameters.length) {
              operation.parameters.forEach((parameter: any) => {
                if (parameter[this.parameterTypeKey] && parameter[this.parameterTypeKey].toLowerCase() === 'formdata') {
                  paramDataType = parameter.type === "array" ? (parameter.items && parameter.items.type) || parameter.type : parameter.type;
                } else {
                  paramDataType = parameter.type;
                }
                operationInfo.parameters.push({
                  "name": parameter.name || (parameter[this.parameterTypeKey] && parameter[this.parameterTypeKey].toLowerCase()),
                  "parameterType": parameter[this.parameterTypeKey],
                  "type": paramDataType
                });
              });
            }
            if (this.isBasicAuthAvailable(securityDefinitions, operation.security, response)) {
              operationInfo.authorization = this.AUTH_BASIC_TYPE;
              operationInfo.parameters.push({
                "name": "wm_auth_username",
                "parameterType": "auth"
              });
              operationInfo.parameters.push({
                "name": "wm_auth_password",
                "parameterType": "auth"
              });
            }
            break;
          }
        }
        if (j < nOps) {
          break;
        }
      }
      /*pass the data prepared to the success callback function*/
      return operationInfo;
    }).catch((errMsg) => {
      return errMsg;
    });
  }

 /**
  * The function `isBasicAuthAvailable` checks if basic authentication is available in the provided
  * security definitions, security object, and Swagger document.
  * @param {any} securityDefinitions - The `securityDefinitions` parameter in the
  * `isBasicAuthAvailable` function is expected to be an object containing security definitions defined
  * in the Swagger document. These security definitions typically include information about
  * authentication methods such as Basic Auth, API keys, OAuth, etc.
  * @param {any} security - The `security` parameter in the `isBasicAuthAvailable` function represents
  * the security requirements defined for the API endpoints in the Swagger specification. It is an
  * object that specifies the security schemes required for the API operations. This parameter is used
  * to check if basic authentication is available based on the security definitions provided
  * @param {any} swagger - Swagger is an open-source software framework that helps developers design,
  * build, document, and consume RESTful web services. It allows users to describe the structure of
  * APIs so that machines can read them.
  * @returns The `isBasicAuthAvailable` function returns a boolean value indicating whether basic
  * authentication is available based on the provided security definitions, security object, and
  * swagger object.
  */
  isBasicAuthAvailable(securityDefinitions: any, security: any, swagger: any): boolean {

    if (!isObject(securityDefinitions)) return false;

    const basicAuthDefinition = Object.entries(securityDefinitions).find(
      ([, value]) => value.type === this.AUTH_BASIC_TYPE
    );

    if (!basicAuthDefinition) return false;

    const [basicAuthKey] = basicAuthDefinition;
    return (
      (security && security[0][basicAuthKey]) ||
      (swagger.security && swagger.security.some((securityObj: any) => basicAuthKey in securityObj))
    );
  }


  isFileServiceRegistered() {
    return new Promise<any>((res, rej) => {
      this.getServicesWithType().then((services: any) => {
        forEach(services, (service: any) => {
          if (service.name === "FileService") {
            res(true);
          }
        });
        res(false);
      });
    });
  }

  addService = (service: any) => {
    let serviceExists = false;
    serviceExists = find(this.services, (existingService: any) => {
      service.name === existingService.name && service.type === existingService.type;
    });
    if (!serviceExists) {
      this.services.push({
        name: service.name,
        type: service.type,
        operations: [],
        url: service.url || "/services/" + service.name,
        sampleResponse: service.sampleResponse
      });
    }
  }
}
